import React, { useEffect, useState } from "react";
import { Button, Form, Offcanvas, Spinner } from "react-bootstrap";
import { Download, ExternalLink, FileText, ImageOff, MapPin, X } from "lucide-react";
import StatusChip from "../../components/statusChip/StatusChip";
import { formatMomentDate } from "../../utils/dateHelper";
import { fetchAttachmentUrl, getFeedback, updateFeedback } from "./feedbackApi";
import {
    CATEGORY_LABELS,
    MODULE_LABELS,
    PLATFORM_LABELS,
    PRODUCTS,
    STATUSES,
    statusMeta,
} from "./constants";

const Row = ({ label, children }) => (
    <div className="d-flex gap-3 py-2 border-bottom">
        <div className="text-muted small text-uppercase fw-medium flex-shrink-0" style={{ width: 110 }}>
            {label}
        </div>
        <div className="small text-break flex-grow-1">{children ?? "-"}</div>
    </div>
);

/** One private screenshot, fetched with the admin's token. */
const Screenshot = ({ product, feedbackId, attachment, onOpen }) => {
    const [url, setUrl] = useState(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let objectUrl = null;
        let cancelled = false;

        fetchAttachmentUrl(product, feedbackId, attachment.id)
            .then((u) => {
                objectUrl = u;
                if (cancelled) URL.revokeObjectURL(u);
                else setUrl(u);
            })
            .catch(() => !cancelled && setFailed(true));

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [product, feedbackId, attachment.id]);

    return (
        <button
            type="button"
            className="border rounded-2 overflow-hidden p-0 bg-light d-flex align-items-center justify-content-center"
            style={{ width: 132, height: 92 }}
            onClick={() => url && onOpen(url)}
            disabled={!url}
            title={attachment.width ? `${attachment.width} × ${attachment.height}` : "Screenshot"}
        >
            {url ? (
                <img src={url} alt="Screenshot" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : failed ? (
                <ImageOff size={20} className="text-muted" />
            ) : (
                <Spinner size="sm" />
            )}
        </button>
    );
};

const fileSize = (bytes) =>
    bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/**
 * A PDF the user attached — usually a map they are contributing. Fetched with
 * the admin's token only when downloaded, and saved under its original name;
 * never rendered inside the admin panel.
 */
const PdfAttachment = ({ product, feedbackId, attachment, toast }) => {
    const [busy, setBusy] = useState(false);

    const download = async () => {
        setBusy(true);
        try {
            const url = await fetchAttachmentUrl(product, feedbackId, attachment.id);
            const link = document.createElement("a");
            link.href = url;
            link.download = attachment.file_name || "attachment.pdf";
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(() => URL.revokeObjectURL(url), 10000);
        } catch (err) {
            toast.error(err.message || "Couldn't download the PDF");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="d-flex align-items-center gap-2 border rounded-2 px-3 py-2 w-100">
            <FileText size={22} className="text-primary flex-shrink-0" />
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <div className="small fw-medium text-truncate" title={attachment.file_name}>
                    {attachment.file_name || "Attachment.pdf"}
                </div>
                <div className="small text-muted">PDF · {fileSize(attachment.size_bytes)}</div>
            </div>
            <Button size="sm" variant="outline-primary" onClick={download} disabled={busy} className="d-flex align-items-center gap-1">
                {busy ? <Spinner size="sm" /> : <Download size={14} />} Download
            </Button>
        </div>
    );
};

/** Module-specific detail. GIS: the sheet and the map view the user was on. */
const GisContext = ({ context = {}, gisWebUrl }) => {
    const { sheet_title, sheet_id, center, zoom, map_type_ids, basemap } = context;

    let mapLink = null;
    if (gisWebUrl && Array.isArray(center)) {
        const params = new URLSearchParams({ center: `${center[0]},${center[1]}` });
        if (sheet_id) params.set("map_ids", sheet_id);
        mapLink = `${gisWebUrl.replace(/\/$/, "")}/maps?${params}`;
    }

    return (
        <>
            <Row label="Sheet">{sheet_title || (sheet_id ? sheet_id : "Not about a specific sheet")}</Row>
            <Row label="Map centre">
                {Array.isArray(center) ? (
                    <span className="d-inline-flex align-items-center gap-1">
                        <MapPin size={13} />
                        {center[1].toFixed(5)}, {center[0].toFixed(5)}
                    </span>
                ) : null}
            </Row>
            <Row label="Zoom">{zoom ?? null}</Row>
            <Row label="Map types">
                {Array.isArray(map_type_ids) ? `${map_type_ids.length} on` : null}
            </Row>
            <Row label="Basemap">{basemap ? basemap.charAt(0).toUpperCase() + basemap.slice(1) : null}</Row>
            {mapLink && (
                <a
                    href={mapLink}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-sm btn-outline-primary mt-3 d-inline-flex align-items-center gap-1"
                >
                    Open this view in the map <ExternalLink size={14} />
                </a>
            )}
        </>
    );
};

const FeedbackDetail = ({ product, feedbackId, canUpdate, currentAdminId, onClose, onChanged, toast }) => {
    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [preview, setPreview] = useState(null);

    useEffect(() => {
        if (!feedbackId) return;
        let cancelled = false;

        setItem(null);
        setError(null);
        setLoading(true);

        getFeedback(product, feedbackId)
            .then((data) => !cancelled && setItem(data))
            .catch((err) => !cancelled && setError(err.message))
            .finally(() => !cancelled && setLoading(false));

        return () => {
            cancelled = true;
        };
    }, [product, feedbackId]);

    const save = async (patch, successMessage) => {
        setSaving(true);
        try {
            const updated = await updateFeedback(product, feedbackId, patch);
            setItem(updated);
            onChanged(updated);
            toast.success(successMessage);
        } catch (err) {
            toast.error(err.message || "Failed to update feedback");
        } finally {
            setSaving(false);
        }
    };

    const status = statusMeta(item?.status);
    const assignedToMe = item?.assignee?.id && item.assignee.id === currentAdminId;

    return (
        <>
            <Offcanvas show={Boolean(feedbackId)} onHide={onClose} placement="end" style={{ width: 520 }}>
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title className="fs-5 fw-semibold">Feedback</Offcanvas.Title>
                </Offcanvas.Header>

                <Offcanvas.Body>
                    {loading && (
                        <div className="text-center py-5">
                            <Spinner />
                        </div>
                    )}

                    {error && <div className="alert alert-danger">{error}</div>}

                    {item && (
                        <>
                            <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                                <StatusChip bg={status.bg} pill title={status.label} />
                                <span className="small text-muted">
                                    {MODULE_LABELS[item.module] || item.module} ·{" "}
                                    {PLATFORM_LABELS[item.platform] || item.platform} ·{" "}
                                    {formatMomentDate(item.created_at, "DD MMM YYYY, hh:mm A")}
                                </span>
                            </div>

                            <h6 className="fw-semibold mb-2">
                                {CATEGORY_LABELS[item.category] || item.category}
                            </h6>
                            <p className="mb-4" style={{ whiteSpace: "pre-wrap" }}>
                                {item.message}
                            </p>

                            {item.attachments?.length > 0 && (
                                <div className="mb-4">
                                    <div className="text-muted small text-uppercase fw-medium mb-2">
                                        Attachments
                                    </div>
                                    <div className="d-flex flex-column gap-2 mb-2">
                                        {item.attachments
                                            .filter((a) => a.mime_type === "application/pdf")
                                            .map((a) => (
                                                <PdfAttachment
                                                    key={a.id}
                                                    product={product}
                                                    feedbackId={item.id}
                                                    attachment={a}
                                                    toast={toast}
                                                />
                                            ))}
                                    </div>
                                    <div className="d-flex gap-2 flex-wrap">
                                        {item.attachments
                                            .filter((a) => a.mime_type !== "application/pdf")
                                            .map((a) => (
                                                <Screenshot
                                                    key={a.id}
                                                    product={product}
                                                    feedbackId={item.id}
                                                    attachment={a}
                                                    onOpen={setPreview}
                                                />
                                            ))}
                                    </div>
                                </div>
                            )}

                            <div className="text-muted small text-uppercase fw-medium mb-1">Sent by</div>
                            <Row label="Name">{item.user_name}</Row>
                            <Row label="Email">{item.user_email}</Row>
                            <Row label="Mobile">{item.user_phone}</Row>
                            <Row label="User ID">{item.user_id || item.external_user_id}</Row>

                            {item.module === "gis" && (
                                <div className="mt-4">
                                    <div className="text-muted small text-uppercase fw-medium mb-1">
                                        Map view when reported
                                    </div>
                                    <GisContext
                                        context={item.context}
                                        gisWebUrl={PRODUCTS[product]?.gisWebUrl}
                                    />
                                </div>
                            )}

                            <div className="mt-4 p-3 bg-light rounded-2">
                                <div className="text-muted small text-uppercase fw-medium mb-2">Triage</div>

                                <Form.Group className="mb-3">
                                    <Form.Label className="small fw-medium">Status</Form.Label>
                                    <Form.Select
                                        size="sm"
                                        value={item.status}
                                        disabled={!canUpdate || saving}
                                        onChange={(e) =>
                                            save({ status: e.target.value }, "Status updated")
                                        }
                                    >
                                        {STATUSES.map((s) => (
                                            <option key={s.value} value={s.value}>
                                                {s.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    {item.resolved_at && (
                                        <Form.Text>
                                            Closed {formatMomentDate(item.resolved_at, "DD MMM YYYY, hh:mm A")}
                                        </Form.Text>
                                    )}
                                </Form.Group>

                                <div className="small fw-medium mb-1">Assigned to</div>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <span className="small">{item.assignee?.name || "Nobody"}</span>
                                    {canUpdate && !assignedToMe && currentAdminId && (
                                        <Button
                                            size="sm"
                                            variant="outline-primary"
                                            disabled={saving}
                                            onClick={() => save({ assigned_to: currentAdminId }, "Assigned to you")}
                                        >
                                            Assign to me
                                        </Button>
                                    )}
                                    {canUpdate && item.assignee && (
                                        <Button
                                            size="sm"
                                            variant="outline-secondary"
                                            disabled={saving}
                                            onClick={() => save({ assigned_to: null }, "Unassigned")}
                                        >
                                            Unassign
                                        </Button>
                                    )}
                                </div>

                                {!canUpdate && (
                                    <div className="small text-muted mt-2">
                                        You can view this feedback but not change it.
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </Offcanvas.Body>
            </Offcanvas>

            {preview && (
                <div
                    role="dialog"
                    aria-label="Screenshot"
                    onClick={() => setPreview(null)}
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-4"
                    style={{ background: "rgba(0,0,0,0.75)", zIndex: 2000, cursor: "zoom-out" }}
                >
                    <button
                        type="button"
                        aria-label="Close"
                        className="btn btn-light position-absolute top-0 end-0 m-3 d-flex p-2"
                        onClick={() => setPreview(null)}
                    >
                        <X size={18} />
                    </button>
                    <img src={preview} alt="Screenshot" style={{ maxWidth: "100%", maxHeight: "100%" }} />
                </div>
            )}
        </>
    );
};

export default FeedbackDetail;
